import React from "react";
import { useState } from "react";
import { Form, DrawerProps, Button, Drawer } from "antd";
import { validateFields } from "../../../../../config/Global";
import { assignErrorToInput } from "../../../../../store/api";
import SVGIcon from "../../../../../utils/SVGIcon";
import Config from "../../../../../config/Config";
import { createRecord } from "../../utils/slice";
import FormComponent from "./FormComponent";
import { useAppDispatch } from "../../../../../utils/hooks/hooks";

interface AddDrawerProps extends DrawerProps {
  titleName: string;
  close: () => void;
}

/**
 * Renders component for adding a new record to the database for UserManagement module.
 *
 * @param {AddDrawerProps} props - The props for the component.
 * @param {string} props.titleName - The name of the title for the drawer.
 * @param {() => void} props.close - The function to close the drawer.
 * @param {...DrawerProps} props.rest - Additional props for the Drawer component.
 * @return {ReactElement} The rendered component.
 */
const AddComponent: React.FC<AddDrawerProps> = ({
  titleName,
  close,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const drawerClose = () => {
    close();
    form.resetFields();
    setDisabled(true)
  };

  // validate form fields and enable/disable save button based on the result
  const validateForm = () => {
    validateFields(form, setDisabled);
  };

  // handle form submission
  const handleFormSubmit = (data: any) => {
    setSaving(true);

    dispatch(createRecord(data))
      .then(() => {
        drawerClose();
      })
      .catch((error: any) => {
        assignErrorToInput(form, error?.STATUS);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Drawer
      title={`Add ${titleName}`}
      placement="right"
      width={"70%"}
      onClose={drawerClose}
      destroyOnClose
      closeIcon={<SVGIcon icon="close" color={Config.themePrimaryBtn} />}
      footer={[
        <Button
          key="2"
          htmlType="button"
          className="cancelBtn"
          onClick={drawerClose}
        >
          Cancel
        </Button>,
        <Button
          key="1"
          disabled={disabled}
          form="addForm"
          loading={saving}
          htmlType="submit"
          type="primary"
        >
          Save
        </Button>,
      ]}
      {...rest}
    >
      <FormComponent
        form={form}
        id="addForm"
        handleSubmit={handleFormSubmit}
        onValuesChange={validateForm}
      />
    </Drawer>
  );
};

export default AddComponent;
